import React, { ReactElement, forwardRef, useImperativeHandle, useRef } from "react";
import { CustomCSSProperties, goToValue } from "../../types";
import { SlideProps } from "./Slide";

interface SliderProps extends React.HTMLAttributes<HTMLUListElement> {
    children: ReactElement<SlideProps>[] | ReactElement<SlideProps>
    className?: string
    loadingText?: string
    style?: CustomCSSProperties
    defaultAt?: number
    vertical?: boolean
}
export interface SliderHandle {
    next: () => void;
    previous: () => void;
    goTo: (index: goToValue) => void;
    getCurrent: () => number
    getSlidesQty: () => number
}

export const Slider = forwardRef<SliderHandle, SliderProps>(({
    children, 
    className, 
    defaultAt = 0, 
    vertical,
    style = {}, 
    loadingText = '',  
    ...rest
}, ref) => {

    const slider = useRef<HTMLUListElement | null>(null)
    const slidesQty = React.Children.count(children)

    const getCurrent = ():number =>  Number(slider.current!.style.getPropertyValue('--current'))
    const next = () => slider.current!.style.setProperty('--current', String(Math.min(getCurrent() + 1, slidesQty - 1)))
    const previous = () => slider.current!.style.setProperty('--current', String(Math.max(getCurrent() - 1, 0)))

    useImperativeHandle(ref, () => ({
        next,
        previous,
        getCurrent,
        goTo(value) {
            if (value === 'start') slider.current!.style.setProperty('--current', '0')
            else if (value === 'end') slider.current!.style.setProperty('--current', String(slidesQty - 1))
            else if (value === 'next') next()
            else if (value === 'previous') previous()
            else if (value >= 0 && value < slidesQty) slider.current!.style.setProperty('--current', String(value))
        },
        getSlidesQty() {
            return slidesQty
        },
    }))

    const orientationClassName = vertical ?
        'vertical fd-c oy-h ox-s' : 'horizontal fd-r oy-s ox-h'

    return (
        <ul 
            aria-hidden
            ref={slider} 
            className={`slider w-100 h-100 p-r df jcfs aic ${orientationClassName} ${className}`} 
            style={{...style, '--current': defaultAt} as CustomCSSProperties}
            {...rest}
        >
            {React.Children.map(children, (child, index) => {
                const style = {...child.props.style, '--index': index}
                return React.cloneElement(child, { style })
                return child
            })}
        </ul>
    )
})